import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const CookieBanner = styled.div<{ show: boolean }>`
  position: fixed;
  bottom: ${(props) => (props.show ? "18px" : "-100%")};
  left: 50%;
  width: min(1080px, calc(100% - 32px));
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(18px);
  box-shadow: 0 20px 55px rgba(16, 33, 63, 0.2);
  z-index: 10000;
  transform: translateX(-50%);
  transition: bottom 0.35s ease;

  /* Force solid background for readability */
  [data-theme="dark"] & {
    background: #1A1A2E;
    border-color: var(--border-color);
  }

  @media (max-width: 768px) {
    bottom: ${(props) => (props.show ? "10px" : "-100%")};
    width: calc(100% - 20px);
  }
`;

const CookieContent = styled.div`
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.85rem;
  }
`;

const CookieText = styled.div`
  flex: 1;

  h4 {
    color: #1A4D8C;
    font-size: 0.98rem;
    margin: 0 0 0.25rem 0;
    font-weight: 600;
    
    [data-theme="dark"] & {
      color: #FFFFFF;
    }
  }

  p {
    color: #2D2D2D;
    font-size: 0.82rem;
    line-height: 1.45;
    margin: 0;
    
    [data-theme="dark"] & {
      color: #E5E5E5;
    }

    a {
      color: #00A896;
      text-decoration: underline;
      font-weight: 600;
      
      &:hover {
        color: #1A4D8C;
      }
      
      [data-theme="dark"] &:hover {
        color: #00C9B0;
      }
    }
  }
`;

const CookieButtons = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const CookieButton = styled.button<{ primary?: boolean }>`
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  ${(props) =>
    props.primary
      ? `
    background: #00A896;
    color: #FFFFFF;
    border: 2px solid #00A896;
    font-weight: 700;
    
    &:hover {
      background: #008F7E;
      border-color: #008F7E;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 168, 150, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
  `
      : `
    background: #FFFFFF;
    color: #1A4D8C;
    border: 2px solid #CCCCCC;
    
    [data-theme="dark"] & {
      background: #2D2D2D;
      color: #FFFFFF;
      border-color: #4D4D4D;
    }
    
    &:hover {
      border-color: #1A4D8C;
      background: #F5F5F5;
      
      [data-theme="dark"] & {
        border-color: #00A896;
        background: #3D3D3D;
      }
    }
  `}

  @media (max-width: 768px) {
    flex: 1;
    width: auto;
    padding: 0.7rem 0.8rem;
  }
`;

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => {
        setShowBanner(true);
      }, 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
    
    // Here you can initialize analytics or other tracking
    console.log("Cookies accepted");
  };

  const handleDecline = () => {
    localStorage.setItem("cookieConsent", "declined");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
    
    // Here you should disable non-essential cookies
    console.log("Cookies declined - only essential cookies will be used");
  };

  const handleCustomize = () => {
    // This could open a modal with detailed cookie settings
    // For now, we'll just accept essentials
    localStorage.setItem("cookieConsent", "customized");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
    alert(t("cookie.saved"));
  };

  return (
    <CookieBanner show={showBanner}>
      <CookieContent>
        <CookieText>
          <h4>{t("cookie.title")}</h4>
          <p>
            {t("cookie.text")}{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              {t("cookie.privacy")}
            </a>
          </p>
        </CookieText>
        <CookieButtons>
          <CookieButton onClick={handleDecline}>
            {t("cookie.decline")}
          </CookieButton>
          <CookieButton onClick={handleCustomize}>
            {t("cookie.customize")}
          </CookieButton>
          <CookieButton primary onClick={handleAccept}>
            {t("cookie.accept")}
          </CookieButton>
        </CookieButtons>
      </CookieContent>
    </CookieBanner>
  );
};

export default CookieConsent;
